import { AuthSession, fetchAuthSession } from "@aws-amplify/core";
import { makeObservableReducer, SyncAction } from "../hooks/hookUtils";
import { shallowEqual } from "@fableous/core/util/Helpers";
import { getCurrentUser } from "aws-amplify/auth";
import logger from "./errorLib";

export type SessionState = {
    authSession: AuthSession | undefined;
    userId: string | undefined;
    identityId : string | undefined;
    userRegion: string | undefined;
    authToken: string | undefined;
    isAuthenticated: boolean;
    isAuthenticating: boolean;
    isAdmin: boolean;
};

export type SessionAction =
    | { type: "setInitialState"; value: SessionState }
    | { type: "setAuthenticated"; value: boolean }
    | SyncAction<SessionState>;

function sessionReducer(state: SessionState, action: SessionAction): SessionState {
    switch (action.type) {
        case "setInitialState":
            if(shallowEqual(state, action.value, ["authSession"])) return state;
            return action.value;
        case "setAuthenticated":
            if (state.isAuthenticated === action.value) return state;
            return { ...state, isAuthenticated: action.value };
        case "__sync__":
            return action.payload
        default:
            return state;
    }
}
const defaultSessionState: SessionState = {
    isAuthenticated: false,
    isAuthenticating: true,
    isAdmin: false,
    authSession: undefined,
    authToken: undefined,
    userId: undefined,
    identityId : undefined,
    userRegion: undefined,
};

let isInitialising = false;

export const sessionStore = makeObservableReducer<SessionState, SessionAction>(sessionReducer, defaultSessionState);

export async function initializeSession(
    dispatch: (action: SessionAction) => void
  ) {
    if (isInitialising) return;
    isInitialising = true;

    const newState = { ...defaultSessionState };

    try {
      logger.debug("Attempting to get current auth user");
      await getCurrentUser();
      const authSession = await fetchAuthSession();
      logger.debug("Retrieved auth session: " + authSession.identityId);
      newState.authSession = authSession;
      newState.isAdmin = checkIsAdmin(authSession);
      newState.identityId = authSession.identityId;
      newState.userRegion = authSession.identityId?.split(":")[0] as string;
      newState.userId = authSession.tokens?.idToken?.payload.sub as string;
      newState.authToken = authSession.tokens?.idToken?.toString() as string;
      newState.isAuthenticated = true;
    } catch {
      // No auth session; remain default
    }

    newState.isAuthenticating = false;
    dispatch({ type: "setInitialState", value: newState });
    isInitialising = false;
  }

  function checkIsAdmin(authSession : AuthSession): boolean {
    const idToken = authSession.tokens?.idToken?.toString(); // decode this token
    if (!idToken) return false;

    const [, payloadBase64] = idToken.split(".");
    if (!payloadBase64) return false;

    const payload = JSON.parse(atob(payloadBase64));
    const groups: string[] = payload["cognito:groups"] || [];
    return groups.includes("Admins");
  }